import React, {ReactElement, useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import './component-item-confirm.scss';
import {ApiNodeItemDryRunResult} from '../../../../interface';
import {useAppSelector} from '../../../../hook/store';
import {NodeTypeState} from '../../../../store/slice/node-type-slice';
import {NodeState} from '../../../../store/slice/node-slice';
import ObjectHelper from '../../../../helpers/object.helper';

interface ComponentItemConfirmProps {
    data: ApiNodeItemDryRunResult,
    handleClose: () => void;
    handleSave: () => void;
}

interface MessageDataItem {
    title: string;
    prevValue: string;
    nextValue: string;
    errorText: string;
    error: boolean;
}


const ComponentItemConfirm = ({data, handleClose, handleSave}: ComponentItemConfirmProps) => {

    const [inProgress, setInProgress] = useState<boolean>(false);
    const [messageData, setMessageData] = useState<MessageDataItem[]>([]);
    const {allNodeTypes} = useAppSelector<NodeTypeState>(store => store.nodeType);
    const {currentNodeItem} = useAppSelector<NodeState>(store => store.node);

    const buildMessage = (): void => {
        if (!!allNodeTypes && !!currentNodeItem && !!data) {
            if (Array.isArray(data.updateReport?.items)) {
                const messageItems: MessageDataItem[] = data.updateReport.items.map(item => {
                    let propValue;
                    const node = allNodeTypes.find(t => t.id === item.nodeType);
                    const props = node?.typeSchema?.properties || [];
                    if (!!props[item.property]) {
                        propValue = props[item.property].title || '-';
                    } else if (item.property === 'name') {
                        propValue = 'Название';
                    } else {
                        propValue = item.customProperty || '-';
                    }
                    return {
                        title: `${item.nodeName} / ${propValue}`,
                        prevValue: !ObjectHelper.isNullOrUndefined(item.originalValue) ? item.originalValue.toString().trim() : '',
                        nextValue: !ObjectHelper.isNullOrUndefined(item.newValue) ? item.newValue.toString().trim() : '',
                        errorText: !ObjectHelper.isNullOrUndefined(item.errorText) ? item.errorText.toString().trim() : '',
                        error: !!item.errorText,
                    };
                });

                setMessageData(messageItems);
            }
        }
    };

    const getValue = (value: any): string | ReactElement => {
        if(value === null){
            return <span style={{fontStyle: 'italic', letterSpacing: '1px'}}>""</span>;
        }
        if(!value.toString().trim()){
            return <span style={{fontStyle: 'italic', letterSpacing: '1px'}}>""</span>;
        }
        return value.toString();
    };

    const onSave = (): void => {
        setInProgress(true);
        handleSave();
    };

    useEffect(() => {
        buildMessage();
    }, [allNodeTypes, currentNodeItem, data]);

    return <>
        <Dialog
            className="component-item-confirm-dialog"
            open={true}
            maxWidth={'lg'}
            fullWidth={true}
            onClose={handleClose}
        >
            <DialogTitle className="dialog-title">
                Подтвердите сохранение значений для &quot;{currentNodeItem?.name || ''}&quot;
                <IconButton
                    aria-label="close"
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[600],
                    }}
                    onClick={handleClose}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent className="dialog-content">
                <div className="dialog-content__title">
                    {!!messageData.length ? 'Изменения в данном компоненте затронут следующие компоненты:' : 'Изменения не найдены.'}

                </div>
                <div className="dialog-content__wrapper">
                    <div className="dialog-content__message">
                        {messageData.map((item, index) => (
                            <div key={index} className={`dialog-content__message__item ${item.error ? 'error' : ''}`}>
                                <div className="title">
                                    <div className="title-wrapper">
                                        <span className="type-name">{item.title}</span>
                                        <span className={'dotted'}></span>
                                        <span className="prev-value">{getValue(item.prevValue)}</span>
                                    </div>
                                </div>
                                <div className="arrow">=&gt;</div>
                                {!item.error && <div className="new-value">{getValue(item.nextValue)}</div>}
                                {item.error && <div className="new-value">{item.errorText}</div>}
                            </div>
                        ))}
                    </div>
                </div>

            </DialogContent>

            <DialogActions className="dialog-buttons">
                {!!messageData.length ? (
                    <>
                        <Button
                            variant={'outlined'}
                            disabled={inProgress}
                            onClick={handleClose}
                        >
                            Отменить
                        </Button>
                        <LoadingButton
                            loading={inProgress}
                            type={'submit'}
                            variant={'contained'}
                            disabled={inProgress || !data?.updateReport?.success}
                            onClick={onSave}
                        >
                            Подтвердить
                        </LoadingButton>
                    </>
                ) : (
                    <Button
                        variant={'outlined'}
                        disabled={inProgress}
                        onClick={handleClose}
                    >
                        Закрыть
                    </Button>
                )}

            </DialogActions>
        </Dialog>
    </>;
};

export {ComponentItemConfirm};
